import type {
  Annotation,
  InlineImageAsset,
  DeprecatedPlanDim,
  Detail,
  LinearDimension,
  LinearDimensionDefinition,
  Linework,
  LineworkDefinition,
  Note,
  NoteDefinition,
  AssetCatalogReference,
  QRCode,
} from "building-types/annotation/annotationTypes";
import {
  AnnotationFillType,
  AnnotationLineType,
  AnnotationType,
  PositioningMethod,
  TextAlignment,
  PlacementXAnchor,
  TextVerticalAlignment,
  PlacementYAnchor,
} from "building-types/annotation/annotationTypesEnum";
import { TICK_EXT, WITNESS_LINE_GAP } from "building-types/constants/dimensionConstants";
import { orientedTickAtPoint } from "building-types/dimensions/dimensionComponentTools";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { Asset } from "building-types/entities/asset";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import type { SnapHostEntity } from "building-types/entities/entityHostedAnnotationTypes";
import { type RoomGeometry } from "building-types/rooms/roomTypes";
import { PDF_DPI } from "common/constants/pdfSettings";
import type { Table } from "common/data/table";
import type { SheetInches, SheetPoints } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import degreeToRadian from "common/utils/degreeToRadian";
import { sheetPointsNew, sheetPointsToInches } from "common/utils/nominalNumbers";
import radianToDegree from "common/utils/radianToDegree";
import { textAlignmentToXAnchor } from "common/utils/textAlignment";
import { AABB2GridPosition } from "geom/aabb2Types";
import { closestParamLine2 } from "geom/closestPoint";
import { DIST_EPSILON, Line2Side } from "geom/core";
import { DIAGONAL_HATCH_SEP_PDF_DPI, GRID_HATCH_SEP_PDF_DPI } from "geom/hatchLibrary";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { line2Normal, line2Side, line2Tangent } from "geom/line2";
import type { Mat3 } from "geom/mat3";
import { mat3Dot, mat3ExtractScaleVectorFrom, mat3Mult, mat3Rotate, mat3Scale, mat3Translate } from "geom/mat3";
import { offsetPolyLine2, polyLine2Thicken } from "geom/offset";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeLength,
  polyLine2Extend,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2ReverseCopy,
  polyLine2TransformCopy,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { ray2Eval, ray2Project } from "geom/ray2";
import { rect2ByCenterWidthHeight, rect2ByThreePoints } from "geom/rect2";
import type { Vec2 } from "geom/vec2";
import {
  vec2Add,
  vec2AngleBetweenCW,
  vec2AngleBetweenCWSimple,
  vec2Dist,
  vec2Perp,
  vec2Scale,
  vec2Dot,
  vec2Sub,
} from "geom/vec2";
import { makeDetailAnnotationSheetGeometry } from "../plans/detailAnnotationGeometry";
import { sheetAssetDimensions } from "../plans/primitives/assetPrimitive";
import { isGeometryRef } from "../roofSystemTools";
import { entityContainsBuiltInLabels } from "../utils/builtInLabelUtils";

export type DimensionPts = [number, number, SnapHostEntity[]];

export function isDeprecatedAnnotation(annotation: Annotation): annotation is DeprecatedPlanDim {
  return annotation.type === AnnotationType.PLAN_DIM;
}

export const annotationTypeLabels = {
  [AnnotationType.LINEAR_DIMENSION]: "Dimension",
  [AnnotationType.NOTE]: "Note",
  [AnnotationType.LINEWORK]: "Linework",
  [AnnotationType.DETAIL]: "Detail",

  // DEPRECATED
  [AnnotationType.PLAN_DIM]: "Plan Dim",
};

export const annotationLineTypeLabels = {
  [AnnotationLineType.CONTINUOUS]: "Continuous",
  [AnnotationLineType.DASHED_LARGE]: "Dashed - Large",
  [AnnotationLineType.DASHED_SMALL]: "Dashed - Small",
  [AnnotationLineType.DOTTED]: "Dotted",
  [AnnotationLineType.CENTERLINE]: "Centerline",
  [AnnotationLineType.NONE]: "None",
};

export const annotationFillTypeLabels: { [key in AnnotationFillType]: string } = {
  [AnnotationFillType.SOLID_WHITE]: "Solid White",
  [AnnotationFillType.SOLID_20PCT_GRAY]: "Solid - 20% Gray",
  [AnnotationFillType.SOLID_40PCT_GRAY]: "Solid - 40% Gray",
  [AnnotationFillType.SOLID_60PCT_GRAY]: "Solid - 60% Gray",
  [AnnotationFillType.CROSSHATCH]: "Crosshatch",
  [AnnotationFillType.DIAGONAL_DOWN]: "Diagonal Down",
  [AnnotationFillType.DIAGONAL_UP]: "Diagonal Up",
  [AnnotationFillType.GRID]: "Grid",
  [AnnotationFillType.NONE]: "None",
};

export interface AnnotationHatchPattern {
  fillStyle: "Hatch";
  hatchMaterialId: HatchMaterialId;
  lineDirection: Vec2;
  lineSpacing: SheetInches;
  crossHatch: boolean;
}

export interface AnnotationSolidPattern {
  fillStyle: "Solid";
  color: string;
}

export type AnnotationFillPattern = AnnotationSolidPattern | AnnotationHatchPattern | { fillStyle: "None" };

/**
 * These values are constructed here because the geom package does not
 * have nominal types and they are defined on the hatches as PDF_DPI values
 */
const diagonalLineSpacing = sheetPointsToInches(sheetPointsNew(DIAGONAL_HATCH_SEP_PDF_DPI));
const gridLineSpacing = sheetPointsToInches(sheetPointsNew(GRID_HATCH_SEP_PDF_DPI));

export const annotationFillPatternByType: { [key in AnnotationFillType]: AnnotationFillPattern } = {
  [AnnotationFillType.SOLID_WHITE]: { fillStyle: "Solid", color: "#ffffff" },
  [AnnotationFillType.SOLID_20PCT_GRAY]: { fillStyle: "Solid", color: "#cccccc" },
  [AnnotationFillType.SOLID_40PCT_GRAY]: { fillStyle: "Solid", color: "#999999" },
  [AnnotationFillType.SOLID_60PCT_GRAY]: { fillStyle: "Solid", color: "#666666" },
  [AnnotationFillType.CROSSHATCH]: {
    fillStyle: "Hatch",
    hatchMaterialId: HatchMaterialId.Annotation_CrossHatch,
    lineDirection: [1, 1],
    lineSpacing: diagonalLineSpacing,
    crossHatch: true,
  },
  [AnnotationFillType.DIAGONAL_DOWN]: {
    fillStyle: "Hatch",
    hatchMaterialId: HatchMaterialId.Annotation_Diagonal_Down,
    lineDirection: [1, -1],
    lineSpacing: diagonalLineSpacing,
    crossHatch: false,
  },
  [AnnotationFillType.DIAGONAL_UP]: {
    fillStyle: "Hatch",
    hatchMaterialId: HatchMaterialId.Annotation_Diagonal_Up,
    lineDirection: [1, 1],
    lineSpacing: diagonalLineSpacing,
    crossHatch: false,
  },
  [AnnotationFillType.GRID]: {
    fillStyle: "Hatch",
    hatchMaterialId: HatchMaterialId.Annotation_Grid,
    lineDirection: [1, 0],
    lineSpacing: gridLineSpacing,
    crossHatch: true,
  },
  [AnnotationFillType.NONE]: { fillStyle: "None" },
};

export interface AnnotationLineDashFormat {
  /**
   * dashLength is the dash-space pattern, and must contain an even number of elements
   * @example dashLength = [1.0, 0.5, 0.5 0.2]
   */
  readonly dashLength: SheetPoints[];
}

export const lineworkDashFormatByType: { [key in AnnotationLineType]?: AnnotationLineDashFormat } = {
  [AnnotationLineType.CENTERLINE]: {
    dashLength: [sheetPointsNew(18), sheetPointsNew(3), sheetPointsNew(4), sheetPointsNew(3)],
  },
  [AnnotationLineType.DASHED_LARGE]: {
    dashLength: [sheetPointsNew(4), sheetPointsNew(8 / 3)],
  },
  [AnnotationLineType.DASHED_SMALL]: {
    dashLength: [sheetPointsNew(8 / 3), sheetPointsNew(8 / 3)],
  },
  [AnnotationLineType.DOTTED]: {
    dashLength: [sheetPointsNew(2 / 3), sheetPointsNew(4 / 3)],
  },
};

export function annotationRotationForbidden(annotation: Annotation): boolean {
  // We don't allow rotating detail images other than asset references.
  // Rotated edge hosted detail images don't work well if the building is mirrored.
  // Asset references cannot be edge hosted so rotating them is allowed.
  // Note that images can still be rotated by rotating the containing drawing template instance.
  // TODO: Consider removing this restriction.
  return annotation.type === AnnotationType.DETAIL && annotation.detailType !== "ASSET_REFERENCE";
}

export function lineworkNew({
  path,
  id,
  name = "Linework",
  labels = {},
  positioningMethod = PositioningMethod.EDGE,
  edgeId = "1",
  lineType = AnnotationLineType.CONTINUOUS,
  lineWeight = 1,
  offset = false,
  offsetDim = 5,
  fillThickness = 8,
  fillType = AnnotationFillType.NONE,
  offsetLineType = AnnotationLineType.CONTINUOUS,
  offsetFillType = AnnotationFillType.NONE,
  disabled,
  parentId,
}: Partial<Linework> & Pick<Linework, "path">): Linework {
  return {
    ...docEntityBaseNew("ANNOTATION", id),
    type: AnnotationType.LINEWORK,
    name,
    path,
    labels,
    positioningMethod,
    edgeId,
    lineType,
    lineWeight,
    offset,
    offsetDim,
    fillThickness,
    fillType,
    offsetLineType,
    offsetFillType,
    disabled,
    parentId,
  };
}

export function polyLineFromShapeOffset(lw: LineworkDefinition): PolyLine2 | undefined {
  if (!lw.path) {
    return undefined;
  }

  if (lw.fillThickness > DIST_EPSILON) {
    return polyLine2Thicken(offsetPolyLine2(lw.path, lw.offsetDim), lw.fillThickness);
  } else {
    return offsetPolyLine2(lw.path, lw.offsetDim);
  }
}

/**
 * @deprecated
 * TODO: replace this with generalized filtering
 */
export function annotationVisibleOnFloorPlan(ele: Annotation) {
  return !(
    entityContainsBuiltInLabels(
      ele,
      "addToFoundationPlan",
      "addToElectricalPlan",
      "addToUtilityPlan",
      "addToRoofPlan",
    ) && !entityContainsBuiltInLabels(ele, "addToFloorPlan")
  );
}

const NOTE_DEFAULT_NAME = "Note";

export type NoteNewParams = Partial<Note> & Pick<Note, "text" | "textBoxWidth" | "textBoxHeight" | "x" | "y">;

export function noteNew({
  x,
  y,
  text,
  textBoxWidth,
  textBoxHeight,
  placementXAnchor = PlacementXAnchor.LEFT,
  placementYAnchor = PlacementYAnchor.CENTER,
  rotation = 0,
  landingLineX = undefined,
  landingLineY = undefined,
  leaderLineX = undefined,
  leaderLineY = undefined,
  leaderLineEndStyle = undefined,
  textAlignment = TextAlignment.LEFT,
  verticalAlignment = TextVerticalAlignment.CENTER,
  name = NOTE_DEFAULT_NAME,
  labels = {},
  fontSize = undefined,
  positioningMethod = PositioningMethod.EDGE,
  edgeId = "1",
  leaderArrowLength,
  leaderArrowWidth,
  disabled,
  id,
}: NoteNewParams): Note {
  return {
    ...docEntityBaseNew("ANNOTATION", id),
    type: AnnotationType.NOTE,
    name: noteAutoName({ name, text }, text),
    text,
    textBoxWidth,
    textBoxHeight,
    x,
    y,
    fontSize,
    placementXAnchor,
    placementYAnchor,
    textAlignment,
    verticalAlignment,
    rotation,
    landingLineX,
    landingLineY,
    leaderLineX,
    leaderLineY,
    leaderLineEndStyle,
    labels,
    positioningMethod,
    edgeId,
    leaderArrowLength,
    leaderArrowWidth,
    disabled,
  };
}

const NOTE_AUTO_NAME_LENGTH = 20;

export function noteIsAutoNamed(note: Pick<Note, "text" | "name">): boolean {
  return note.text.startsWith(note.name.substring(0, NOTE_AUTO_NAME_LENGTH));
}

export function noteAutoName(note: Pick<Note, "name" | "text">, newText: string): string {
  if (!note.name || note.name === NOTE_DEFAULT_NAME || note.name === "" || noteIsAutoNamed(note)) {
    return newText?.substring(0, NOTE_AUTO_NAME_LENGTH);
  }
  return note.name;
}

export function inlineImageDefaultWidthHeight(
  imagePixelWidth: number | undefined,
  imagePixelHeight: number | undefined,
  parentHandleType: DocEntityType,
): {
  width: number;
  height: number;
  assetAspectRatio: number;
} {
  imagePixelWidth = imagePixelWidth ?? 1;
  imagePixelHeight = imagePixelHeight ?? 1;
  const SCALE_DOWN_SIZE_THRESHOLD = 1200;
  const normalizeScaleFactor =
    imagePixelWidth > SCALE_DOWN_SIZE_THRESHOLD || imagePixelHeight > SCALE_DOWN_SIZE_THRESHOLD ? 0.25 : 1;
  // If we're adding to a page scale context, then scale the image down to an appropriate size.
  // If we're in world space, don't scale the image down.
  const pageSpaceScaleFactor =
    parentHandleType === "CONTENT_TEMPLATE" || parentHandleType === "CONTENT_TEMPLATE_INSTANCE" ? 1 / PDF_DPI : 1;
  return {
    width: imagePixelWidth * normalizeScaleFactor * pageSpaceScaleFactor,
    height: imagePixelHeight * normalizeScaleFactor * pageSpaceScaleFactor,
    assetAspectRatio: imagePixelWidth / imagePixelHeight,
  };
}

export function inlineImageAssetDetailNew({
  x,
  y,
  url,
  width,
  height,
  assetAspectRatio,
  rotation = 0,
  name = "",
  labels = {},
  positioningMethod = PositioningMethod.EDGE,
  edgeId = "1",
  disabled,
}: Partial<InlineImageAsset> & Pick<InlineImageAsset, "width" | "height" | "assetAspectRatio" | "x" | "y">): Detail {
  return {
    ...docEntityBaseNew("ANNOTATION"),
    type: AnnotationType.DETAIL,
    detailType: "INLINE_IMAGE",
    name,
    url,
    width,
    height,
    assetAspectRatio,
    x,
    y,
    rotation,
    labels,
    positioningMethod,
    edgeId,
    disabled,
  };
}

export function assetCatalogReferenceDetailNew({
  x,
  y,
  assetId,
  scale = 1,
  rotation = 0,
  name = "",
  labels = {},
  positioningMethod = PositioningMethod.EDGE,
  edgeId = "1",
  disabled,
}: Partial<AssetCatalogReference> & Pick<AssetCatalogReference, "x" | "y">): Detail {
  return {
    ...docEntityBaseNew("ANNOTATION"),
    type: AnnotationType.DETAIL,
    detailType: "ASSET_REFERENCE",
    name,
    assetId,
    scale,
    x,
    y,
    rotation,
    labels,
    positioningMethod,
    edgeId,
    disabled,
  };
}

export function qrCodeDetailNew({
  x,
  y,
  size = 2,
  name = "",
  url = "",
  labels = {},
  positioningMethod = PositioningMethod.EDGE,
  edgeId = "1",
  disabled,
}: Partial<QRCode> & Pick<QRCode, "x" | "y">): Detail {
  return {
    ...docEntityBaseNew("ANNOTATION"),
    type: AnnotationType.DETAIL,
    detailType: "QR_CODE",
    rotation: 0,
    name,
    url,
    size,
    x,
    y,
    labels,
    positioningMethod,
    edgeId,
    disabled,
  };
}

export function linearDimensionNew({
  name = "",
  startX,
  startY,
  endX,
  endY,
  lineStartX,
  lineStartY,
  lineEndX,
  lineEndY,
  orientation,
  labels = {},
  positioningMethod = PositioningMethod.EDGE,
  edgeId = "1",
  fontSize,
  tickLength,
  extension,
  witnessGap,
  dimensionValue,
  prefix,
  suffix,
  top,
  bottom,
  entityHostedStart,
  entityHostedEnd,
  disabled,
  showEntityHostedLock,
}: Partial<LinearDimension> &
  Pick<
    LinearDimension,
    "startX" | "startY" | "endX" | "endY" | "lineStartX" | "lineStartY" | "lineEndX" | "lineEndY" | "orientation"
  >): LinearDimension {
  return {
    ...docEntityBaseNew("ANNOTATION"),
    type: AnnotationType.LINEAR_DIMENSION,
    name,
    startX,
    startY,
    endX,
    endY,
    lineStartX,
    lineStartY,
    lineEndX,
    lineEndY,
    orientation,
    labels,
    positioningMethod,
    edgeId,
    fontSize,
    tickLength,
    extension,
    witnessGap,
    dimensionValue,
    prefix,
    suffix,
    top,
    bottom,
    entityHostedStart,
    entityHostedEnd,
    disabled,
    showEntityHostedLock,
  };
}

/**
 * Checks if the given linear dimension definition satisfies the invariants.
 * @param dim The linear dimension definition to check.
 * @returns True if the dimension definition satisfies the invariants, false otherwise.
 */
export function linearDimensionDefinitionCheckInvariants(dim: LinearDimensionDefinition): boolean {
  // Checks if numeric fields are NaN
  if (
    isNaN(dim.startX) ||
    isNaN(dim.startY) ||
    isNaN(dim.endX) ||
    isNaN(dim.endY) ||
    isNaN(dim.lineStartX) ||
    isNaN(dim.lineStartY) ||
    isNaN(dim.lineEndX) ||
    isNaN(dim.lineEndY)
  ) {
    return false;
  }

  // Check if the distance between the line start and end points is less than a small value.
  // If it is, then the dimension definition is invalid and should be rejected.
  if (vec2Dist(dim.lineStartX, dim.lineStartY, dim.lineEndX, dim.lineEndY) < DIST_EPSILON) {
    return false;
  }

  // form normal to witness line - this should be possible due to the check above
  const [nx, ny] = line2Normal(dim.lineStartX, dim.lineStartY, dim.lineEndX, dim.lineEndY);

  // Verifies the ray formed by the lineStart and start contain the start pt

  const [lineStartX, lineStartY] = ray2Project(dim.lineStartX, dim.lineStartY, nx, ny, dim.startX, dim.startY);
  if (vec2Dist(lineStartX, lineStartY, dim.startX, dim.startY) > DIST_EPSILON) {
    return false;
  }

  // Verifies the ray formed by the lineEnd and end contain the end pt
  const [lineEndX, lineEndY] = ray2Project(dim.lineEndX, dim.lineEndY, nx, ny, dim.endX, dim.endY);
  if (vec2Dist(lineEndX, lineEndY, dim.endX, dim.endY) > DIST_EPSILON) {
    return false;
  }

  return true;
}

export function isInvariantLinearDimension(annotation: Annotation): boolean {
  return annotation.type === "LINEAR_DIMENSION" && !linearDimensionDefinitionCheckInvariants(annotation);
}

function vec2BelowLine(startX: number, startY: number, endX: number, endY: number, testX: number, testY: number) {
  if (endX > startX) {
    return line2Side(startX, startY, endX, endY, testX, testY) === Line2Side.RIGHT;
  }

  if (endX === startX) {
    return false;
  }

  return line2Side(startX, startY, endX, endY, testX, testY) === Line2Side.LEFT;
}

interface LinearDimensionFromThreePointsParams
  extends Pick<
    LinearDimension,
    | "startX"
    | "startY"
    | "endX"
    | "endY"
    | "fontSize"
    | "witnessGap"
    | "extension"
    | "tickLength"
    | "entityHostedStart"
    | "entityHostedEnd"
  > {
  offsetX: number;
  offsetY: number;
  name?: string;
  edgeId?: string;
  positioningMethod?: PositioningMethod;
}

export function linearDimensionFromThreePoints(options: LinearDimensionFromThreePointsParams) {
  const { startX, startY, endX, endY, offsetX, offsetY } = options;

  const l = vec2Dist(startX, startY, endX, endY);
  const t = closestParamLine2(startX, startY, endX, endY, offsetX, offsetY, false);

  if (t < 0) {
    if (vec2BelowLine(startX, startY, endX, endY, offsetX, offsetY)) {
      return linearDimensionNew({
        ...options,
        lineStartX: offsetX,
        lineStartY: startY,
        lineEndX: offsetX,
        lineEndY: endY,
        orientation: "VERTICAL",
      });
    } else {
      return linearDimensionNew({
        ...options,
        lineStartX: startX,
        lineStartY: offsetY,
        lineEndX: endX,
        lineEndY: offsetY,
        orientation: "HORIZONTAL",
      });
    }
  } else if (t > l) {
    if (vec2BelowLine(startX, startY, endX, endY, offsetX, offsetY)) {
      return linearDimensionNew({
        ...options,
        lineStartX: startX,
        lineStartY: offsetY,
        lineEndX: endX,
        lineEndY: offsetY,
        orientation: "HORIZONTAL",
      });
    } else {
      return linearDimensionNew({
        ...options,
        lineStartX: offsetX,
        lineStartY: startY,
        lineEndX: offsetX,
        lineEndY: endY,
        orientation: "VERTICAL",
      });
    }
  }

  const [nx, ny] = line2Normal(startX, startY, endX, endY);

  const orientedOffsetDist = vec2Dot(nx, ny, offsetX - startX, offsetY - startY);

  const [lineStartX, lineStartY] = ray2Eval(startX, startY, nx, ny, orientedOffsetDist);
  const [lineEndX, lineEndY] = ray2Eval(endX, endY, nx, ny, orientedOffsetDist);

  return linearDimensionNew({
    ...options,
    lineStartX,
    lineStartY,
    lineEndX,
    lineEndY,
    orientation: "ALIGNED",
  });
}
interface BuildLinearDimensionParams {
  readonly threePtParams: LinearDimensionFromThreePointsParams;
  readonly addlParams?: {
    readonly lineStartX: number;
    readonly lineStartY: number;
    readonly lineEndX: number;
    readonly lineEndY: number;
    readonly orientation: "ALIGNED" | "HORIZONTAL" | "VERTICAL";
  };
}

export function buildLinearDimension(props: BuildLinearDimensionParams) {
  const { threePtParams, addlParams } = props;

  if (!addlParams) {
    return linearDimensionFromThreePoints(threePtParams);
  }
  const { lineStartX, lineStartY, lineEndX, lineEndY, orientation } = addlParams;
  return linearDimensionNew({
    ...threePtParams,
    lineStartX,
    lineStartY,
    lineEndX,
    lineEndY,
    orientation,
  });
}
const LINEAR_DIMENSION_EXTENSION = 2;

// Dimensions are built like so:
//
//              X'-X" <= label
// --/------(lengthEdge)--------/<=(tick)--
//   |                          |
//   |                          |<=(witness)
//     <= (witnessGap)
//   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~ <= (dimensioned thing)
interface DimensionPolyLines {
  readonly lengthEdges: PolyLine2[];
  readonly witnesses: PolyLine2[];
  readonly ticks: PolyLine2[];
}

export function linearDimensionToPolyLine2sWithTicks({
  startX,
  startY,
  endX,
  endY,
  lineStartX,
  lineStartY,
  lineEndX,
  lineEndY,
  tickLength = TICK_EXT,
  witnessGap = WITNESS_LINE_GAP,
  extension = LINEAR_DIMENSION_EXTENSION,
  startExt = 0,
  endExt = 0,
}: Omit<LinearDimensionDefinition, "type" | "orientation">): DimensionPolyLines {
  const [tx, ty] = line2Tangent(lineStartX, lineStartY, lineEndX, lineEndY);
  const dimPls = [
    polyLine2Extend(polyLine2ByTwoPoints(lineStartX, lineStartY, lineEndX, lineEndY), extension, extension),
  ].filter((x) => polyLine2EdgeLength(x, 0) > DIST_EPSILON);

  const witnessPls = [
    polyLine2Extend(polyLine2ByTwoPoints(startX, startY, lineStartX, lineStartY), -witnessGap + startExt, extension),
    polyLine2Extend(polyLine2ByTwoPoints(endX, endY, lineEndX, lineEndY), -witnessGap + endExt, extension),
  ].filter((x) => polyLine2EdgeLength(x, 0) > DIST_EPSILON);

  const tickPls = [
    orientedTickAtPoint(tx, ty, lineEndX, lineEndY, tickLength),
    orientedTickAtPoint(tx, ty, lineStartX, lineStartY, tickLength),
  ].filter((x) => polyLine2EdgeLength(x, 0) > DIST_EPSILON);

  return { lengthEdges: dimPls, witnesses: witnessPls, ticks: tickPls };
}

export function roomAnnotationTableFlipOverYAxis(
  linework: Table<Annotation[]>,
  assetCatalog: Table<Asset>,
  moveX: number,
) {
  if (!linework) {
    return linework;
  }

  const flipped = {};

  for (const id in linework) {
    flipped[id] = annotationArrayFlipOverYAxis(linework[id], assetCatalog, moveX);
  }

  return flipped as Table<Annotation[]>;
}

function annotationArrayFlipOverYAxis(annotation: Annotation[], assetCatalog: Table<Asset>, moveX: number = 0) {
  if (!annotation) {
    return annotation;
  }

  return annotation.map((x) => annotationMirror(x, assetCatalog, moveX));
}

export function annotationMirror(annot: Annotation, assetCatalog: Table<Asset>, moveX: number = 0): Annotation {
  switch (annot.type) {
    case AnnotationType.LINEAR_DIMENSION: {
      const pl = polyLine2Move(
        polyLine2ReflectOverYAxis(
          polyLine2New([
            annot.startX,
            annot.startY,
            annot.endX,
            annot.endY,
            annot.lineStartX,
            annot.lineStartY,
            annot.lineEndX,
            annot.lineEndY,
          ]),
        ),
        moveX,
        0,
      );

      return {
        ...annot,
        startX: pl[0],
        startY: pl[1],
        endX: pl[2],
        endY: pl[3],
        lineStartX: pl[4],
        lineStartY: pl[5],
        lineEndX: pl[6],
        lineEndY: pl[7],
      };
    }
    case AnnotationType.LINEWORK: {
      if (annot.path) {
        return {
          ...annot,
          path: polyLine2Move(polyLine2ReflectOverYAxis(polyLine2ReverseCopy(annot.path)), moveX, 0),
        };
      }
      return annot;
    }
    case AnnotationType.DETAIL: {
      const box = polyLine2Move(polyLine2ReflectOverYAxis(detailOutlineBoxToPolyLine2(annot, assetCatalog)), moveX, 0);
      const aabb = polyLine2AABB2(box);

      return {
        ...annot,
        x: aabb.minX,
        y: aabb.minY,
      };
    }
    case AnnotationType.NOTE: {
      const { placementXAnchor, textAlignment } = annot;
      let { leaderLineX, landingLineX } = annot;

      const x = -annot.x + moveX;
      if (typeof leaderLineX === "number") {
        const xOffset = annot.x - leaderLineX;
        leaderLineX = x + xOffset;
      }

      if (typeof landingLineX === "number") {
        const xOffset = annot.x - landingLineX;
        landingLineX = x + xOffset;
      }

      const tx = Math.cos(degreeToRadian(annot.rotation));
      const ty = -Math.sin(degreeToRadian(annot.rotation));

      const rotation = radianToDegree(vec2AngleBetweenCWSimple(1, 0, tx, ty));

      return {
        ...annot,
        x,
        leaderLineX,
        landingLineX,
        rotation,
        textAlignment: flipHorizontalTextAlignment(textAlignment),
        placementXAnchor: flipPlacementXAnchor(placementXAnchor),
      };
    }
    case AnnotationType.PLAN_DIM: {
      if (isGeometryRef(annot.polyLine)) {
        return annot;
      }

      return {
        ...annot,
        polyLine: polyLine2Move(polyLine2ReflectOverYAxis(polyLine2ReverseCopy(annot.polyLine)), moveX, 0),
      };
    }
    default:
      assertUnreachable(annot);
  }
}

export function flipHorizontalTextAlignment(textAlignment: TextAlignment): TextAlignment {
  const alignmentToXAnchor = textAlignmentToXAnchor(textAlignment);
  return alignmentToXAnchor ? getTextAlignmentFromXAnchor(flipPlacementXAnchor(alignmentToXAnchor)) : textAlignment;
}

export function getTextAlignmentFromXAnchor(placementXAnchor: PlacementXAnchor): TextAlignment {
  switch (placementXAnchor) {
    case PlacementXAnchor.LEFT:
      return TextAlignment.LEFT;
    case PlacementXAnchor.CENTER:
      return TextAlignment.CENTER;
    case PlacementXAnchor.RIGHT:
      return TextAlignment.RIGHT;
  }
}

export function flipPlacementXAnchor(placementXAnchor: PlacementXAnchor): PlacementXAnchor {
  switch (placementXAnchor) {
    case PlacementXAnchor.LEFT:
      return PlacementXAnchor.RIGHT;
    case PlacementXAnchor.CENTER:
      return PlacementXAnchor.CENTER;
    case PlacementXAnchor.RIGHT:
      return PlacementXAnchor.LEFT;
  }
}

export function hostEdgeOriginAngle(host: RoomGeometry, edgeId: string | undefined, mirror: boolean = false) {
  if (host?.edgeIds === undefined || !edgeId) {
    return undefined;
  }

  const edgeIndex = host.edgeIds.indexOf(edgeId);
  if (edgeIndex < 0) {
    return undefined;
  }

  const startIndex = edgeIndex * 2;
  let originX = host.outline[startIndex];
  let originY = host.outline[startIndex + 1];
  const endX = host.outline[startIndex + 2];
  const endY = host.outline[startIndex + 3];

  const angle = vec2AngleBetweenCW(endX - originX, endY - originY, 1, 0);

  if (mirror === true) {
    originX = endX;
    originY = endY;
  }

  return { originX, originY, angle };
}

export function globalToHostEdgeTransform(originX: number, originY: number, angle: number, mirror = false) {
  let mat = mat3Rotate(mirror ? angle : -angle);
  if (mirror) {
    mat = mat3Mult(mat, mat3Scale(-1, 1));
  }
  return mat3Mult(mat, mat3Translate(-originX, -originY));
}

export function hostEdgeToGlobalTransform(originX: number, originY: number, angle: number, mirror = false) {
  let mat = mat3Translate(originX, originY);
  if (mirror) {
    mat = mat3Mult(mat, mat3Scale(-1, 1));
  }
  mat = mat3Mult(mat, mat3Rotate(angle));

  return mat;
}

export function annotationDuplicate(annotation: Annotation): Annotation | undefined {
  switch (annotation.type) {
    case AnnotationType.LINEAR_DIMENSION:
      return linearDimensionNew({
        ...annotation,
      });
    case AnnotationType.LINEWORK:
      return lineworkNew({
        ...annotation,
        path: annotation.path ? polyLine2Copy(annotation.path) : undefined,
      });
    case AnnotationType.NOTE:
      return noteNew({
        ...annotation,
      });
    case AnnotationType.DETAIL:
      if (annotation.detailType === "INLINE_IMAGE") {
        return inlineImageAssetDetailNew({
          ...annotation,
        });
      }
      if (annotation.detailType === "QR_CODE") {
        return qrCodeDetailNew({
          ...annotation,
        });
      }
      return assetCatalogReferenceDetailNew({
        ...annotation,
      });
    case AnnotationType.PLAN_DIM:
      console.warn(`${annotation.type} has been deprecated`);
      break;
  }
  return undefined;
}

export function transformLinearDimensionDefinition(
  dim: LinearDimensionDefinition,
  mat: Mat3,
): LinearDimensionDefinition {
  const [lineStartX, lineStartY] = mat3Dot(mat, dim.lineStartX, dim.lineStartY);
  const [lineEndX, lineEndY] = mat3Dot(mat, dim.lineEndX, dim.lineEndY);
  const [startX, startY] = mat3Dot(mat, dim.startX, dim.startY);
  const [endX, endY] = mat3Dot(mat, dim.endX, dim.endY);

  return {
    ...dim,
    lineStartX,
    lineStartY,
    lineEndX,
    lineEndY,
    startX,
    startY,
    endX,
    endY,
  };
}

export function transformLineworkDefinition(linework: LineworkDefinition, mat: Mat3): LineworkDefinition {
  if (linework.path) {
    return {
      ...linework,
      path: polyLine2TransformCopy(linework.path, mat),
    };
  }

  return linework;
}

export function transformNoteDefinition(note: NoteDefinition, mat: Mat3): NoteDefinition {
  let { leaderLineX, leaderLineY, landingLineX, landingLineY } = note;

  if (typeof leaderLineX === "number" && typeof leaderLineY === "number") {
    [leaderLineX, leaderLineY] = mat3Dot(mat, leaderLineX, leaderLineY);
  }

  if (typeof landingLineX === "number" && typeof landingLineY === "number") {
    [landingLineX, landingLineY] = mat3Dot(mat, landingLineX, landingLineY);
  }

  const [x, y] = mat3Dot(mat, note.x, note.y);

  // compute the rotation of the note
  const [rx2, ry2] = mat3Dot(mat, note.x + 1, note.y);
  const [dx, dy] = vec2Sub(rx2, ry2, x, y);
  const ang = vec2AngleBetweenCWSimple(1, 0, dx, dy);

  return {
    ...note,
    leaderLineX,
    leaderLineY,
    landingLineX,
    landingLineY,
    x,
    y,
    rotation: (note.rotation ?? 0) + radianToDegree(ang),
  };
}

/**
 * "corrects" the rotation angle of a note to be within the range [-180, 180]
 * @param rotation angle in radians
 * @returns corrected rotation angle in radians in the range [-180, 180]
 */
function readableNoteRotation(rotation: number): number {
  rotation = rotation % 360;
  if (rotation > 180) {
    rotation -= 360;
  }
  if (rotation < -180) {
    rotation += 360;
  }
  return rotation;
}

export function annotationTransform(annotation: Annotation, mat: Mat3, angleDegrees: number): Annotation {
  switch (annotation.type) {
    case AnnotationType.LINEWORK: {
      const newLineworkDefinition = transformLineworkDefinition(annotation, mat);
      return {
        ...annotation,
        ...newLineworkDefinition,
      };
    }
    case AnnotationType.NOTE: {
      const rotation = readableNoteRotation(annotation.rotation + angleDegrees);
      const newNoteDefinition = transformNoteDefinition(annotation, mat);
      return {
        ...annotation,
        ...newNoteDefinition,
        rotation,
      };
    }
    case AnnotationType.DETAIL: {
      const rotation = (annotation.rotation ?? 0) + angleDegrees;
      const [x, y] = mat3Dot(mat, annotation.x, annotation.y);
      const [sx] = mat3ExtractScaleVectorFrom(mat) ?? [1, 1];
      if (annotation.detailType === "INLINE_IMAGE") {
        return {
          ...annotation,
          x,
          y,
          rotation,
          // Note: We don't support non-uniform scaling of images.
          width: annotation.width * sx,
          height: annotation.height * sx,
        };
      } else if (annotation.detailType === "QR_CODE") {
        // QR codes cannot be rotated.
        return {
          ...annotation,
          x,
          y,
          size: annotation.size * sx,
        };
      }
      return {
        ...annotation,
        x,
        y,
        rotation,
        scale: annotation.scale * sx,
      };
    }
    case AnnotationType.LINEAR_DIMENSION: {
      const newDimensionDefinition = transformLinearDimensionDefinition(annotation, mat);
      return {
        ...annotation,
        ...newDimensionDefinition,
      };
    }
    default: {
      return {
        ...annotation,
      };
    }
  }
}

export function noteTextBoxToPolyLine2(note: Note | NoteDefinition): PolyLine2 {
  if (!note.textBoxWidth || note.textBoxWidth <= 0 || !note.textBoxHeight || note.textBoxHeight <= 0) {
    return rect2ByCenterWidthHeight(note.x, note.y, 0, 0);
  }

  const { x, y, rotation, textBoxHeight, textBoxWidth } = note;
  let tx = Math.cos(rotation ?? 0);
  let ty = Math.sin(rotation ?? 0);
  const [nx, ny] = vec2Perp(tx, ty);
  const [offsetX, offsetY] = vec2Scale(nx, ny, textBoxHeight / 2);

  if (note.placementXAnchor === PlacementXAnchor.RIGHT) {
    tx = -tx;
    ty = -ty;
  } else if (note.placementXAnchor === PlacementXAnchor.CENTER) {
    tx = -tx / 2;
    ty = -ty / 2;
  }

  const [bottomStartX, bottomStartY] = vec2Add(x, y, offsetX, offsetY);
  const [bottomEndX, bottomEndY] = vec2Add(x, y, tx * textBoxWidth + offsetX, ty * textBoxWidth + offsetY);
  const [topX, topY] = vec2Add(x, y, -offsetX, -offsetY);

  return rect2ByThreePoints(bottomStartX, bottomStartY, bottomEndX, bottomEndY, topX, topY);
}

export type LeaderDefinition = Pick<
  NoteDefinition,
  "leaderLineX" | "leaderLineY" | "landingLineX" | "landingLineY" | "x" | "y" | "leaderArrowWidth" | "leaderArrowLength"
>;

export function noteLeaderToPolyLine2(note: LeaderDefinition): PolyLine2 | undefined {
  if (typeof note.leaderLineX === "number" && typeof note.leaderLineY === "number") {
    if (typeof note.landingLineX === "number" && typeof note.landingLineY === "number") {
      return polyLine2New([note.leaderLineX, note.leaderLineY, note.landingLineX, note.landingLineY, note.x, note.y]);
    } else {
      return polyLine2New([note.leaderLineX, note.leaderLineY, note.x, note.y]);
    }
  }
  return undefined;
}

export function detailOutlineBoxToPolyLine2(detail: Detail, assetCatalog: Table<Asset>): PolyLine2 {
  const { x, y } = detail;
  const geom = makeDetailAnnotationSheetGeometry(detail, assetCatalog);
  if (!geom) {
    return rect2ByCenterWidthHeight(x, y, 0, 0);
  }

  const { outline } = sheetAssetDimensions(geom);
  return outline;
}

/**
 * This function implements a rule based method to determine the orientation of the dimension
 * line. The rule is based on grid position placed in in any of the 9 axes-aligned grid
 * boxes (see AABB2GridPosition):
 * 1. If the aspect ratio is close to 0, the dimension line is to be drawn vertical
 * 2. If the aspect ratio is close to infinity, the dimension line is to be drawn horizontal
 * 3. If the aspect ratio is less than 1, the dimension line is horizontal if the gridPosition is in the
 *    middle, top column, bottom column, top left, top right, bottom left, bottom right. Otherwise
 *   the dimension line is drawn vertical.
 * 4. If the aspect ratio is greater than 1, the dimension line is vertical if the gridPosition is in the
 *   middle, left column, right column. Otherwise, the dimension line is horizontal.
 *
 */
export function computeDimensionOrientation(
  gridPosition: AABB2GridPosition,
  aspectRatio: number,
): "VERTICAL" | "HORIZONTAL" {
  if (aspectRatio < 0.01) {
    return "VERTICAL"; //almost vertical line
  } else if (aspectRatio > 100) {
    return "HORIZONTAL"; //almost horizontal line
  }

  if (aspectRatio < 1) {
    if (
      gridPosition === AABB2GridPosition.MC ||
      gridPosition === AABB2GridPosition.TC ||
      gridPosition === AABB2GridPosition.BC ||
      gridPosition === AABB2GridPosition.TL ||
      gridPosition === AABB2GridPosition.TR ||
      gridPosition === AABB2GridPosition.BL ||
      gridPosition === AABB2GridPosition.BR
    ) {
      return "HORIZONTAL";
    } else {
      return "VERTICAL";
    }
  } else {
    if (
      gridPosition === AABB2GridPosition.MC ||
      gridPosition === AABB2GridPosition.ML ||
      gridPosition === AABB2GridPosition.MR ||
      gridPosition === AABB2GridPosition.TL ||
      gridPosition === AABB2GridPosition.TR ||
      gridPosition === AABB2GridPosition.BL ||
      gridPosition === AABB2GridPosition.BR
    ) {
      return "VERTICAL";
    } else {
      return "HORIZONTAL";
    }
  }
}
